/**
 * L'image affichée quand un lien du site est partagé (WhatsApp, Facebook, LinkedIn…).
 *
 * Ces robots n'exécutent AUCUN JavaScript : ils lisent le HTML servi et prennent la
 * première balise `og:image`. Si son URL est invalide, ils n'affichent rien du tout —
 * ils ne se rabattent pas sur une autre balise.
 *
 * Relevé du 30/09/2026 sur les 312 pages pré-rendues, qui a motivé ce fichier :
 *  - l'URL par défaut d'index.html contenait des espaces NON ENCODÉS. Elle n'était même
 *    pas analysable : tout lien partagé sortait sans image. Une fois encodée, la même
 *    URL répond HTTP 200 ;
 *  - 23 pages déclaraient une URL RELATIVE (« /lovable-uploads/… »), invalide pour un
 *    robot qui n'a pas de page de référence ;
 *  - la page d'une offre déclarait une chaîne VIDE quand l'offre n'avait pas d'image ;
 *  - une page pointait vers `maisoneridia.lovable.app`, le domaine de préproduction ;
 *  - une autre vers un SVG, qu'aucun robot social ne sait rendre.
 *
 * D'où cette fonction unique, plutôt qu'une rustine par page.
 */

const SITE = 'https://maisoneridia.fr';

/** Le visuel de marque, utilisé quand la page n'a pas d'image à elle. Espaces encodés. */
export const IMAGE_SOCIALE_DEFAUT =
  'https://storage.googleapis.com/gpt-engineer-file-uploads/LK7ed01SQ8Pb8PfpXIIvIWirRH72' +
  '/social-images/social-1769909233294-Logo%20Maison%20Eridia%20Event%20Planner.png';

/** Le format d'aperçu attendu par Facebook, LinkedIn et WhatsApp. */
const LARGEUR = 1200;
const HAUTEUR = 630;

/**
 * Pexels et Unsplash savent redimensionner et ré-encoder à la volée. On leur demande un
 * JPEG de 1200×630 plutôt que le fichier d'origine.
 *
 * Mesuré le 30/09/2026 sur la plus lourde du site :
 *   telle quelle                     36,18 Mo, image/png
 *   avec w=1200&h=630 seulement       1,26 Mo, image/png
 *   avec fm=jpg en plus                 86 Ko, image/jpeg
 *
 * Soit 430 fois plus léger. WhatsApp renonce à télécharger un aperçu bien avant 36 Mo,
 * et le JPEG évite au passage la question du WebP, que WhatsApp ne rend pas.
 */
const redimensionnerSiPossible = (u: string): string => {
  const sansParametres = u.split('?')[0];

  if (/^https:\/\/images\.pexels\.com\//i.test(u)) {
    return `${sansParametres}?auto=compress&cs=tinysrgb&fm=jpg&w=${LARGEUR}&h=${HAUTEUR}&fit=crop`;
  }
  if (/^https:\/\/images\.unsplash\.com\//i.test(u)) {
    return `${sansParametres}?fm=jpg&q=80&w=${LARGEUR}&h=${HAUTEUR}&fit=crop`;
  }
  // Notre stockage Supabase ne sait PAS transformer : la fonctionnalité est désactivée
  // sur le projet (403 vérifié sur l'endpoint /render/image/). Ces images-là ne peuvent
  // être allégées qu'au téléversement.
  return u;
};

/**
 * Rend une URL d'image exploitable par un robot social : absolue, sur le bon domaine,
 * sans espace brut, et allégée quand l'hébergeur sait le faire. Renvoie le visuel de
 * marque quand rien d'utilisable n'est fourni.
 */
export const imageSociale = (url?: string | null): string => {
  const brut = (url ?? '').trim();
  if (!brut) return IMAGE_SOCIALE_DEFAUT;

  // Un SVG ne s'affiche dans aucun aperçu de lien : mieux vaut le visuel de marque.
  if (brut.split('?')[0].toLowerCase().endsWith('.svg')) return IMAGE_SOCIALE_DEFAUT;

  // Le domaine de préproduction ne doit pas fuiter dans un partage.
  const sansPreprod = brut.replace('https://maisoneridia.lovable.app', SITE);

  const absolue = /^https?:\/\//i.test(sansPreprod)
    ? sansPreprod
    : `${SITE}/${sansPreprod.replace(/^\/+/, '')}`;

  // Uniquement les espaces : un encodage complet casserait les URL déjà encodées,
  // en transformant « %20 » en « %2520 ».
  return redimensionnerSiPossible(absolue.replace(/ /g, '%20'));
};
